import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { Plan, Subscription } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe, useSession } from '../session/SessionProvider';
import { daysLabel, formatPrice, INTERVAL_LABELS, INTERVAL_PERIOD, isLapsed, STATUS_LABELS } from './format';
import { BillingHistory } from './BillingHistory';
import { PlanPicker } from './PlanPicker';

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

/**
 * Facturación de la Iglesia activa (requiere billing.manage; el backend lo vuelve a validar).
 * KEORA no ve ni guarda datos de tarjeta: el pago ocurre en la página del proveedor.
 */
export function BillingPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const sub = useAsync(
    (signal) => (churchId ? api.get<{ subscription: Subscription | null }>(`/saas/churches/${churchId}/subscription`, signal).then((r) => r.subscription) : Promise.resolve(null)),
    [churchId],
  );

  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Plan y pagos</h1>
        {me.activeContext ? <p className="muted">Suscripción de {me.activeContext.churchName}.</p> : null}
      </header>
      {!churchId ? <EmptyState title="Elige primero la iglesia en la que actúas." /> : null}
      {sub.status === 'loading' ? <Loading /> : null}
      {sub.status === 'error' ? <ErrorState error={sub.error} onRetry={sub.reload} /> : null}
      {churchId && sub.status === 'ready' && sub.data ? <SubscriptionCard churchId={churchId} subscription={sub.data} onChange={sub.reload} /> : null}
      {churchId && sub.status === 'ready' && !sub.data ? <ChoosePlanCard churchId={churchId} onChosen={sub.reload} /> : null}
    </div>
  );
}

/** Nombre visible de cada proveedor de pagos. */
const PROVIDER_LABELS: Record<string, string> = {
  mercadopago: 'Mercado Pago (tarjeta, OXXO, SPEI)',
  stripe: 'Tarjeta (Stripe)',
};

function SubscriptionCard({ churchId, subscription: s, onChange }: { churchId: string; subscription: Subscription; onChange: () => void }) {
  const { refresh } = useSession();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState<'pay' | 'simulate' | 'interval' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);
  const returnedFromSimulatedCheckout = s.simulatedPayments && params.has('simulado');
  /** Resultado con el que el proveedor de pagos regresa a esta página: aprobado, pendiente, rechazado o cancelado. */
  const providerResult = params.get('pago');

  // El pago aprobado se acredita cuando llega el aviso de Mercado Pago (segundos después):
  // se vuelve a consultar la suscripción unas veces hasta verlo reflejado.
  useEffect(() => {
    if (providerResult !== 'aprobado') return;
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      onChange();
      void refresh();
      if (tries >= 10) window.clearInterval(timer);
    }, 3000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerResult]);

  async function pay(provider?: string) {
    setBusy('pay');
    setError(null);
    try {
      const { url } = await api.post<{ url: string }>(`/saas/churches/${churchId}/subscription/checkout`, provider ? { provider } : {});
      window.location.assign(url);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(null);
    }
  }

  async function simulate() {
    setBusy('simulate');
    setError(null);
    try {
      await api.post(`/saas/churches/${churchId}/subscription/simulated-payment`);
      setParams({}, { replace: true });
      setPaid(true);
      await refresh(); // los permisos efectivos cambian al reactivarse
      onChange();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  const lapsed = isLapsed(s.status);
  const realProviders = (s.paymentProviders ?? []).filter((p) => p !== 'simulated');
  return (
    <>
      {paid ? <Alert tone="success">Pago registrado. Gracias.</Alert> : null}
      {providerResult === 'aprobado' ? (
        <Alert tone="success">Tu pago fue aprobado. Gracias. En unos segundos se verá reflejado aquí.</Alert>
      ) : null}
      {providerResult === 'pendiente' ? (
        <Alert tone="info">
          Tu pago quedó pendiente (por ejemplo, en OXXO o por transferencia). Se activará en cuanto se confirme; no tienes que
          hacer nada más aquí.
        </Alert>
      ) : null}
      {providerResult === 'rechazado' ? <Alert>No se pudo cobrar. Intenta de nuevo con otro medio de pago.</Alert> : null}
      {providerResult === 'cancelado' ? <Alert tone="info">No se hizo ningún cobro. Puedes pagar cuando quieras.</Alert> : null}
      {s.status === 'pending' ? (
        <Alert tone="info">
          Tu iglesia está pendiente de activación. Se activa en cuanto pagues tu plan, o cuando la administración de KEORA la active. Mientras tanto
          puedes consultar, pero no modificar datos.
        </Alert>
      ) : lapsed ? (
        <Alert>
          {s.status === 'trial_expired' ? 'La prueba terminó.' : s.status === 'past_due' ? 'El pago está vencido y terminó el periodo de gracia.' : 'La suscripción está cancelada.'} Tu
          iglesia conserva todos sus datos y puede consultarlos, pero no modificarlos hasta que se pague. La página pública sigue activa.
        </Alert>
      ) : null}
      {s.status === 'grace' ? (
        <Alert tone="info">Tu pago venció. Todo sigue funcionando hasta el {formatDate(s.graceEndsAt ?? null)}: paga antes para no perder acceso.</Alert>
      ) : null}
      {s.status === 'courtesy' ? <Alert tone="success">KEORA te concedió la plataforma sin costo hasta el {formatDate(s.courtesyUntil ?? null)}.</Alert> : null}
      {s.members && s.members.limit !== null && s.members.count >= s.members.limit ? (
        <Alert tone="info">
          Tu iglesia tiene {s.members.count} miembros activos y tu plan incluye {s.members.limit}. No se pueden aprobar ni registrar más miembros hasta que cambies de plan o
          agregues miembros adicionales. Escríbenos para ampliar la capacidad.
        </Alert>
      ) : null}
      <Card title={s.plan.name} action={<span className={`badge ${lapsed ? 'badge--warn' : 'badge--active'}`}>{STATUS_LABELS[s.status]}</span>}>
        <dl className="details">
          <dt>{s.price ? `Pago ${INTERVAL_LABELS[s.price.interval]?.toLowerCase()}` : 'Mensualidad'}</dt>
          <dd>
            <strong>{formatPrice(s.price?.totalCents ?? s.plan.monthlyPriceCents, s.plan.currency)}</strong>
            {s.price && (s.price.addonsCents || s.price.discountCents) ? (
              <span className="small muted block">
                Plan {formatPrice(s.price.baseCents, s.plan.currency)}
                {s.price.addonsCents ? ` + extras ${formatPrice(s.price.addonsCents, s.plan.currency)}` : ''}
                {s.price.discountCents ? ` − ${s.price.discountPercent}% de descuento (${formatPrice(s.price.discountCents, s.plan.currency)})` : ''}
              </span>
            ) : null}
          </dd>
          {s.members ? (
            <>
              <dt>Miembros</dt>
              <dd>
                {s.members.count} activos{s.members.limit !== null ? ` de ${s.members.limit} incluidos` : ' (sin límite)'}
                {s.members.limit !== null ? <progress className="usage" max={s.members.limit} value={Math.min(s.members.count, s.members.limit)} /> : null}
              </dd>
            </>
          ) : null}
          {s.addons && s.addons.length ? (
            <>
              <dt>Extras</dt>
              <dd>{s.addons.map((a) => `${a.description}${a.quantity > 1 ? ` ×${a.quantity}` : ''}`).join(', ')}</dd>
            </>
          ) : null}
          {s.status === 'trialing' ? (
            <>
              <dt>Prueba</dt>
              <dd>
                Te quedan <strong>{daysLabel(s.daysLeft)}</strong> (termina el {formatDate(s.trialEndsAt)})
              </dd>
            </>
          ) : null}
          {s.status === 'trial_expired' ? (
            <>
              <dt>Prueba</dt>
              <dd>Terminó el {formatDate(s.trialEndsAt)}</dd>
            </>
          ) : null}
          {s.status === 'active' || s.status === 'past_due' || s.status === 'grace' ? (
            <>
              <dt>Pagado hasta</dt>
              <dd>{formatDate(s.currentPeriodEnd)}</dd>
            </>
          ) : null}
        </dl>
        {error ? <Alert>{error}</Alert> : null}
        {s.intervals && s.intervals.length > 1 && s.status !== 'canceled' ? (
          <fieldset className="choices choices--row">
            <legend>¿Cada cuánto pagas?</legend>
            {s.intervals.map((i) => (
              <label key={i.interval} className="choice">
                <input
                  type="radio"
                  name="interval"
                  checked={s.interval === i.interval}
                  disabled={busy !== null}
                  onChange={async () => {
                    if (!window.confirm(`¿Cambiar a pago ${INTERVAL_LABELS[i.interval]?.toLowerCase()} (${formatPrice(i.totalCents, s.plan.currency)})?`)) return;
                    setBusy('interval');
                    setError(null);
                    try {
                      await api.put(`/saas/churches/${churchId}/subscription/interval`, { interval: i.interval });
                      onChange();
                    } catch (e) {
                      setError(errorMessage(e));
                    } finally {
                      setBusy(null);
                    }
                  }}
                />
                <span>
                  {INTERVAL_LABELS[i.interval]} · {formatPrice(i.totalCents, s.plan.currency)}
                </span>
              </label>
            ))}
          </fieldset>
        ) : null}
        {s.status !== 'canceled' && s.status !== 'courtesy' && (s.price?.totalCents ?? 1) > 0 ? (
          <div className="actions">
            {returnedFromSimulatedCheckout ? (
              <Button busy={busy === 'simulate'} onClick={simulate}>
                Simular pago confirmado
              </Button>
            ) : s.canPay ? (
              realProviders.length > 1 ? (
                <>
                  <p className="small muted billing__choose">
                    {s.status === 'active' ? `Pagar ${INTERVAL_PERIOD[s.interval ?? 'month']}` : `Pagar ${formatPrice(s.price?.totalCents ?? s.plan.monthlyPriceCents, s.plan.currency)}`} con:
                  </p>
                  {realProviders.map((p, i) => (
                    <Button key={p} variant={i === 0 ? 'primary' : 'secondary'} busy={busy === 'pay'} onClick={() => pay(p)}>
                      {PROVIDER_LABELS[p] ?? p}
                    </Button>
                  ))}
                </>
              ) : (
                <Button busy={busy === 'pay'} onClick={() => pay(realProviders[0])}>
                  {s.status === 'active' ? `Pagar ${INTERVAL_PERIOD[s.interval ?? 'month']}` : `Pagar ${formatPrice(s.price?.totalCents ?? s.plan.monthlyPriceCents, s.plan.currency)}`}
                </Button>
              )
            ) : (
              <p className="muted small">Los pagos en línea aún no están disponibles. La administración de KEORA te indicará cómo pagar.</p>
            )}
          </div>
        ) : null}
        {s.simulatedPayments ? <p className="muted small">Modo de desarrollo: los pagos son simulados, no se cobra nada.</p> : null}
      </Card>
      <ModulesCard churchId={churchId} subscription={s} onChange={onChange} />
      <BillingHistory churchId={churchId} currency={s.plan.currency} />
    </>
  );
}

/** Módulos extra con costo mensual (p. ej. cuestionarios con IA): la iglesia los contrata o los deja aquí. */
function ModulesCard({ churchId, subscription: s, onChange }: { churchId: string; subscription: Subscription; onChange: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const modules = (s.modules ?? []).filter((m) => m.available || m.contracted);
  if (modules.length === 0 || s.status === 'canceled') return null;

  async function toggle(code: string, contract: boolean) {
    setBusy(code);
    setError(null);
    try {
      if (contract) await api.post(`/saas/churches/${churchId}/subscription/modules/${code}`);
      else await api.delete(`/saas/churches/${churchId}/subscription/modules/${code}`);
      onChange();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card title="Módulos extra">
      {error ? <Alert>{error}</Alert> : null}
      <ul className="list">
        {modules.map((m) => (
          <li key={m.code} className="list__item list__item--actions">
            <span>
              <strong>{m.name}</strong>
              <span className="small muted block">
                {m.monthlyPriceCents !== null ? `${formatPrice(m.monthlyPriceCents, s.plan.currency)} al mes` : ''} · hasta {m.monthlyLimit} {m.unit} al mes
                {m.contracted ? ` · este mes: ${m.used} de ${m.monthlyLimit}` : ''}
              </span>
              {m.code === 'ai_quizzes' && !m.contracted ? (
                <span className="small muted block">Los maestros de Raíces generan un cuestionario a partir de las notas y versículos de la clase, y lo revisan antes de publicarlo.</span>
              ) : null}
            </span>
            {m.contracted ? (
              <Button
                variant="ghost"
                busy={busy === m.code}
                onClick={() => {
                  if (window.confirm(`¿Dejar «${m.name}»? Ya no se cobrará en tu siguiente pago y la función se bloquea.`)) void toggle(m.code, false);
                }}
              >
                Dejar
              </Button>
            ) : (
              <Button
                busy={busy === m.code}
                onClick={() => {
                  if (window.confirm(`¿Contratar «${m.name}» por ${formatPrice(m.monthlyPriceCents ?? 0, s.plan.currency)} al mes? Se suma a tu pago.`)) void toggle(m.code, true);
                }}
              >
                Contratar
              </Button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Iglesias registradas antes de existir los planes: su administrador elige uno aquí. */
function ChoosePlanCard({ churchId, onChosen }: { churchId: string; onChosen: () => void }) {
  const { refresh } = useSession();
  const plans = useAsync((signal) => api.get<{ items: Plan[] }>('/saas/plans', signal).then((r) => r.items), []);
  const [planCode, setPlanCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function choose() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/saas/churches/${churchId}/subscription`, { planCode });
      await refresh();
      onChosen();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Elige un plan">
      <p className="muted">Tu iglesia aún no tiene plan. Empieza con la prueba gratuita del plan que elijas.</p>
      {plans.status === 'loading' ? <Loading /> : null}
      {plans.status === 'error' ? <ErrorState error={plans.error} onRetry={plans.reload} /> : null}
      {plans.status === 'ready' ? (
        plans.data.length === 0 ? (
          <EmptyState title="Todavía no hay planes publicados." />
        ) : (
          <>
            <PlanPicker plans={plans.data} value={planCode} onChange={setPlanCode} />
            {error ? <Alert>{error}</Alert> : null}
            <Button busy={busy} disabled={!planCode} onClick={choose} className="btn--block">
              Empezar prueba
            </Button>
          </>
        )
      ) : null}
    </Card>
  );
}
